import React, { useState, useEffect } from 'react';
import './StudyFlashcards.css';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../supabase';
import { signInWithGoogle, handleRedirectResult } from '../utils/authUtils';
import { triggerFlashcardAnalyticsUpdate } from '../utils/analytics';
import { 
    ChevronLeft, 
    ChevronRight, 
    Play, 
    Pause, 
    Volume2, 
    Star, 
    Lightbulb, 
    Settings, 
    X,
    RotateCcw,
    Maximize2,
    BookOpen,
    ChevronLeftCircle,
    ChevronRightCircle,
    Check
} from 'lucide-react';

/** Flashcards saved from Notes use `note:<uuid>` as document_name — hide from the header. */
function getDisplayDocumentName(documentName) {
    if (!documentName || typeof documentName !== 'string') return '';
    const t = documentName.trim();
    if (/^note:[0-9a-f-]{36}$/i.test(t)) return '';
    return t;
}

const StudyFlashcards = () => {
    const { uid, flashcardId } = useParams();
    const navigate = useNavigate();
    const { user, authInitialized } = useAuth();

    // State management
    const [flashcardSet, setFlashcardSet] = useState(null);
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [trackProgress, setTrackProgress] = useState(false);
    const [showHint, setShowHint] = useState(false);
    const [favorites, setFavorites] = useState(new Set());
    const [showProgress, setShowProgress] = useState(false);
    
    // Progress tracking
    const [memorized, setMemorized] = useState(new Set());
    const [stillLearning, setStillLearning] = useState(new Set());
    const [analyticsUpdateTimeout, setAnalyticsUpdateTimeout] = useState(null);




    // Fetch flashcard data from Supabase
    useEffect(() => {
        const fetchFlashcardData = async () => {
            try {
                setLoading(true);
                console.log('🔍 Fetching flashcard data for ID:', flashcardId);
                
                // Get Supabase user ID from Firebase UID
                const supabaseUser = await database.getUserByFirebaseUid(uid);
                if (!supabaseUser) {
                    throw new Error('User not found in Supabase');
                }

                // Fetch the specific flashcard set
                const flashcardData = await database.getFlashcardById(flashcardId);
                console.log('✅ Flashcard data loaded:', flashcardData);
                
                if (flashcardData && flashcardData.user_id === supabaseUser.id) {
                    setFlashcardSet(flashcardData);
                    
                    // Load existing progress for this flashcard set
                    try {
                        const progressData = await database.getFlashcardProgress(supabaseUser.id, flashcardId);
                        console.log('📊 Loaded flashcard progress:', progressData);
                        
                        // Convert progress data to Sets for UI
                        const memorizedSet = new Set();
                        const stillLearningSet = new Set();
                        
                        progressData.forEach(progress => {
                            if (progress.status === 'memorized') {
                                memorizedSet.add(progress.card_index);
                            } else if (progress.status === 'still_learning') {
                                stillLearningSet.add(progress.card_index);
                            }
                        });
                        
                        setMemorized(memorizedSet);
                        setStillLearning(stillLearningSet);
                    } catch (progressError) {
                        console.error('❌ Error loading flashcard progress:', progressError);
                        // Continue without progress data
                    }
                } else {
                    throw new Error('Flashcard not found or access denied');
                }
            } catch (err) {
                console.error('❌ Error fetching flashcard data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (user && flashcardId) {
        fetchFlashcardData();
        }
    }, [user, flashcardId, uid]);

    // Cleanup analytics timeout on unmount
    useEffect(() => {
        return () => {
            if (analyticsUpdateTimeout) {
                clearTimeout(analyticsUpdateTimeout);
            }
        };
    }, [analyticsUpdateTimeout]);

    // Navigation functions
    const goToPrevious = () => {
        if (currentCardIndex > 0) {
            setCurrentCardIndex(currentCardIndex - 1);
            setIsFlipped(false);
            setShowHint(false);
        }
    };

    const goToNext = () => {
        if (flashcardSet && currentCardIndex < flashcardSet.flashcard_data.length - 1) {
            setCurrentCardIndex(currentCardIndex + 1);
            setIsFlipped(false);
            setShowHint(false);
        }
    };

    const flipCard = () => {
        setIsFlipped(!isFlipped);
    };

    const toggleFavorite = () => {
        const newFavorites = new Set(favorites);
        if (favorites.has(currentCardIndex)) {
            newFavorites.delete(currentCardIndex);
        } else {
            newFavorites.add(currentCardIndex);
        }
        setFavorites(newFavorites);
    };

    const toggleAutoplay = () => {
        setIsPlaying(!isPlaying);
    };

    const toggleProgress = () => {
        const newState = !trackProgress;
        setTrackProgress(newState);
        setShowProgress(newState); // Keep them in sync
    };

    // Debounced analytics update for flashcard progress
    const debouncedAnalyticsUpdate = () => {
        if (analyticsUpdateTimeout) {
            clearTimeout(analyticsUpdateTimeout);
        }
        
        const timeout = setTimeout(async () => {
            if (!user || !flashcardSet) return;
            
            try {
                const supabaseUser = await database.getUserByFirebaseUid(user.uid);
                if (!supabaseUser) return;
                
                // Get current progress for this flashcard set
                const currentProgress = await database.getFlashcardProgress(supabaseUser.id, flashcardSet.id);
                
                await triggerFlashcardAnalyticsUpdate(supabaseUser.id, flashcardSet.document_name, currentProgress);
                console.log('✅ Analytics updated after flashcard study session');
            } catch (error) {
                console.error('❌ Analytics update failed:', error);
            }
        }, 2000); // Wait 2 seconds after last interaction
        
        setAnalyticsUpdateTimeout(timeout);
    };

    // Save flashcard progress to Supabase
    const saveFlashcardProgress = async (cardIndex, status) => {
        if (!user || !flashcardSet) return;
        
        try {
            // Get Supabase user ID
            const supabaseUser = await database.getUserByFirebaseUid(user.uid);
            if (!supabaseUser) {
                console.log('User not found in Supabase, skipping progress save');
                return;
            }

            // Calculate mastery level based on status
            let masteryLevel = 0;
            if (status === 'memorized') masteryLevel = 100;
            else if (status === 'still_learning') masteryLevel = 50;

            const progressData = {
                flashcardId: flashcardSet.id,
                cardIndex: cardIndex,
                status: status,
                studyCount: 1, // Increment study count
                correctCount: status === 'memorized' ? 1 : 0,
                masteryLevel: masteryLevel
            };

            console.log('💾 Saving flashcard progress:', progressData);
            await database.saveFlashcardProgress(supabaseUser.id, progressData);
            console.log('✅ Flashcard progress saved successfully');
            
            // Trigger debounced analytics update
            debouncedAnalyticsUpdate();
        } catch (error) {
            console.error('❌ Failed to save flashcard progress:', error);
        }
    };

    const markAsStillLearning = () => {
        const newStillLearning = new Set(stillLearning);
        newStillLearning.add(currentCardIndex);
        setStillLearning(newStillLearning);
        
        // Remove from memorized if it was there
        const newMemorized = new Set(memorized);
        newMemorized.delete(currentCardIndex);
        setMemorized(newMemorized);
        
        // Save progress to Supabase
        saveFlashcardProgress(currentCardIndex, 'still_learning');
        
        // Auto-advance to next card
        goToNext();
    };

    const markAsMemorized = () => {
        const newMemorized = new Set(memorized);
        newMemorized.add(currentCardIndex);
        setMemorized(newMemorized);
        
        // Remove from still learning if it was there
        const newStillLearning = new Set(stillLearning);
        newStillLearning.delete(currentCardIndex);
        setStillLearning(newStillLearning);
        
        // Save progress to Supabase
        saveFlashcardProgress(currentCardIndex, 'memorized');
        
        // Auto-advance to next card
        goToNext();
    };

    // Show loading state
    if (loading) {
        return (
            <div className="study-flashcards-container">
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading flashcards...</p>
                </div>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div className="study-flashcards-container">
                <div className="error-state">
                    <h2>Error Loading Flashcards</h2>
                    <p>{error}</p>
                    <button onClick={() => navigate('/my-library/' + uid)} className="btn-primary">
                        Back to Library
                    </button>
                </div>
            </div>
        );
    }

    // Show no data state
    if (!flashcardSet || !flashcardSet.flashcard_data || flashcardSet.flashcard_data.length === 0) {
        return (
            <div className="study-flashcards-container">
                <div className="error-state">
                    <h2>No Flashcards Found</h2>
                    <p>This flashcard set appears to be empty.</p>
                    <button onClick={() => navigate('/my-library/' + uid)} className="btn-primary">
                        Back to Library
                    </button>
                </div>
            </div>
        );
    }

    const currentCard = flashcardSet.flashcard_data[currentCardIndex];
    const totalCards = flashcardSet.flashcard_data.length;
    const documentNameForHeader = getDisplayDocumentName(flashcardSet.document_name);

    // Clean flashcard text to remove **FRONT** and **BACK** markers
    const cleanFlashcardText = (text) => {
        if (!text) return '';
        return text
            .replace(/^\*\*Front:\*\*\s*/g, '')
            .replace(/^\*\*Back:\*\*\s*/g, '')
            .replace(/^Front:\s*/g, '')
            .replace(/^Back:\s*/g, '')
            .replace(/^-\s*\*\*Front:\*\*\s*/g, '')
            .replace(/^-\s*\*\*Back:\*\*\s*/g, '')
            .replace(/^-\s*Front:\s*/g, '')
            .replace(/^-\s*Back:\s*/g, '')
            .trim();
    };

    return (
        <div className="study-flashcards-container">
            {/* Header */}
            <div className="study-header">
                <div className="header-left">
                    <button onClick={() => navigate('/my-library/' + uid)} className="dropdown-btn">
                        <BookOpen size={16} />
                        <span>Library</span>
                        <ChevronLeft size={14} />
                    </button>
                </div>
                
                <div className="header-center">
                    <span className="card-counter">{currentCardIndex + 1}/{totalCards}</span>
                    {documentNameForHeader ? (
                        <span className="document-name">{documentNameForHeader}</span>
                    ) : null}
                </div>
                
                <div className="header-right">
                    {/* <button className="header-btn" title="Turn into questions">
                        <RotateCcw size={16} />
                        <span>Turn into questions</span>
                    </button> */}
                    {/* <button className="header-btn" title="Settings">
                        <Settings size={16} />
                    </button> */}
                    {/* <button className="header-btn" onClick={() => navigate('/my-library/' + uid)} title="Close">
                        <X size={16} />
                    </button> */}
                </div>
            </div>

            {showProgress && (
                <div className="progress-row">
                    <div className="progress-item still-learning">
                        <div className="progress-number">{stillLearning.size}</div>
                        <p className="progress-label still-learning-label">Still Learning</p>
                        </div>

                    <div className="progress-item memorized">
                        <div className="progress-number">{memorized.size}</div>
                        <p className="progress-label">Memorized</p>
                    </div>
                </div>
            )}

            
            {/* Main Content */}
            <div className="study-content">
                {/* Flashcard */}
                <div className="flashcard-container">
                    <div 
                        className={`flashcard ${isFlipped ? 'flipped' : ''}`}
                        onClick={flipCard}
                    >
                        <div className="flashcard-inner">
                            {/* Front of card */}
                            <div className="flashcard-front">
                                <div className="card-header">
                                    <button 
                                        className="hint-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowHint(!showHint);
                                        }}
                                    >
                                        <Lightbulb size={16} />
                                        <span>Get a hint</span>
                                    </button>
                                    <div className="card-actions">
                                        <button className="action-btn" title="Audio">
                                            <Volume2 size={16} />
                                        </button>
                                        <button 
                                            className={`action-btn ${favorites.has(currentCardIndex) ? 'favorited' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleFavorite();
                                            }}
                                            title="Favorite"
                                        >
                                            <Star size={16} />
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="card-content">
                                    <p className="card-text">{cleanFlashcardText(currentCard.front)}</p>
                                </div>
                                
                                <div className="card-footer">
                                    <span className="flip-hint">Click the card to flip</span>
                                </div>
                            </div>

                            {/* Back of card */}
                            <div className="flashcard-back">
                                <div className="card-header">
                                    <span className="back-label">Answer</span>
                                    <div className="card-actions">
                                        <button className="action-btn" title="Audio">
                                            <Volume2 size={16} />
                                        </button>
                                        <button 
                                            className={`action-btn ${favorites.has(currentCardIndex) ? 'favorited' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleFavorite();
                                            }}
                                            title="Favorite"
                                        >
                                            <Star size={16} />
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="card-content">
                                    <p className="card-text">{cleanFlashcardText(currentCard.back)}</p>
                                </div>
                                
                                <div className="card-footer">
                                    {trackProgress ? (
                                        <div className="progress-buttons">
                                            <button 
                                                className="progress-btn still-learning-btn still-learning-btn-progress"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    markAsStillLearning();
                                                }}
                                            >
                                               <X color="red" size={20} />
                                            </button>
                                            <button 
                                                className="progress-btn memorized-btn memorized-btn-progress"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    markAsMemorized();
                                                }}
                                            >
                                                <Check color="green" size={20} />
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="flip-hint">Click the card to flip</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Controls */}
            <div className="study-controls">
                <div className="controls-left">
                    <div className="progress-toggle">
                        <span onClick={toggleProgress}>Track progress</span>
                        <button 
                            className={`toggle-switch ${trackProgress ? 'active' : ''}`}
                            onClick={toggleProgress}
                        >
                            <div className="toggle-slider"></div>
                        </button>
                    </div>
                </div>
                
                <div className="controls-center">
                    <button 
                        className="nav-btn"
                        onClick={goToPrevious}
                        disabled={currentCardIndex === 0}
                    >
                        {/* <ChevronLeftCircle size={20} /> */}
                        <p>Back</p>
                    </button>
                    <button 
                        className="nav-btn"
                        onClick={goToNext}
                        disabled={currentCardIndex === totalCards - 1}
                    >
                        {/* <ChevronRightCircle size={20} /> */}
                        <p>Next</p>
                    </button>
                </div>
                
                {/* <div className="controls-right">
                    <button 
                        className={`play-btn ${isPlaying ? 'playing' : ''}`}
                        onClick={toggleAutoplay}
                        title={isPlaying ? 'Pause autoplay' : 'Start autoplay'}
                    >
                        {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                    </button>
                    <button className="expand-btn" title="Fullscreen">
                        <Maximize2 size={16} />
                    </button>
                </div> */}
            </div>
        </div>
    );
};

export default StudyFlashcards;